if (!customElements.get('its-product-notebook')) {
  customElements.define('its-product-notebook', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.book = this.querySelector('.its-notebook__book');
      this.stage = this.querySelector('.its-notebook__stage');
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.status = this.querySelector('.its-notebook__status');
      this.pages = [...this.querySelectorAll('[data-page]')];
      if (!this.book || !this.pages.length) return;
      this.position = this.position || 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.classList.contains('is-ready')) {
        this.sheets = [];
        const frontCover = this.querySelector('[data-cover="front"]');
        const backCover = this.querySelector('[data-cover="back"]');
        this.hasFrontCover = Boolean(frontCover);
        this.hasBackCover = Boolean(backCover);
        if (frontCover) this.addSheet([frontCover, null], true);
        for (let i = 0; i < this.pages.length; i += 2) {
          this.addSheet([this.pages[i], this.pages[i + 1]]);
        }
        if (backCover) this.addSheet([null, backCover], true);
        this.classList.add('is-ready');
      }
      this.maximum = this.sheets.length - (this.sheets.at(-1).children[1].querySelector('.its-notebook__page') ? 0 : 1);
      this.buildWire();
      this.coverOptions = [...this.querySelectorAll('[data-cover-option]')];
      this.coverOptions.forEach(button => button.addEventListener('click', () => this.selectCover(button), options));
      const picker = this.querySelector('.its-notebook__cover-picker');
      if (picker) picker.hidden = this.coverOptions.length < 2;
      this.chapters = [...this.querySelectorAll('[data-chapter]')];
      this.chapters.forEach(button => button.addEventListener('click', () => this.navigateTo(button), options));
      const contents = this.querySelector('.its-notebook__chapters');
      if (contents) contents.hidden = false;
      this.querySelector('.its-notebook__controls').hidden = false;
      this.querySelector('.its-notebook__hint').hidden = this.maximum === 0;
      this.previous.addEventListener('click', () => this.turn(-1), options);
      this.next.addEventListener('click', () => this.turn(1), options);
      this.stage.addEventListener('keydown', event => {
        const direction = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
        if (!direction) return;
        event.preventDefault();
        this.turn(direction);
      }, options);
      this.stage.addEventListener('pointerdown', event => this.startDrag(event), options);
      this.stage.addEventListener('pointermove', event => this.moveDrag(event), options);
      this.stage.addEventListener('pointerup', event => this.endDrag(event), options);
      this.stage.addEventListener('pointercancel', event => this.endDrag(event, true), options);
      this.stage.addEventListener('lostpointercapture', event => this.endDrag(event, true), options);
      this.stage.addEventListener('dragstart', event => event.preventDefault(), options);
      window.addEventListener('resize', () => this.updateWireMask(this.wireMaskTurn?.sheet, this.wireMaskTurn?.angle), options);
      this.render();
    }

    buildWire() {
      if (this.querySelector('.its-notebook__wire') || !this.dataset.wireSrc) return;
      const rawCount = this.dataset.wireRings;
      const parsed = rawCount === undefined || rawCount === '' ? 14 : Number(rawCount);
      const count = Number.isFinite(parsed) ? Math.max(0, Math.min(40, Math.floor(parsed))) : 14;
      if (count === 0) return;
      this.book.style.setProperty('--wire-count', count);
      const wire = document.createElement('div');
      wire.className = 'its-notebook__wire';
      wire.setAttribute('aria-hidden', 'true');
      wire.style.zIndex = this.sheets.length + 3;
      wire.style.setProperty('--wire-count', count);
      for (let i = 0; i < count; i++) {
        const ring = document.createElement('img');
        ring.className = 'its-notebook__wire-ring';
        ring.src = this.dataset.wireSrc;
        ring.alt = '';
        ring.width = 64;
        ring.height = 28;
        ring.draggable = false;
        ring.style.top = `${count === 1 ? 50 : 5 + i * 90 / (count - 1)}%`;
        wire.append(ring);
      }
      this.book.append(wire);
      this.wire = wire;
      this.wireCount = count;
      this.sheets.forEach(sheet => {
        [...sheet.children].slice(0, 2).forEach((face, side) => {
          const punches = document.createElement('div');
          punches.className = `its-notebook__punches${side ? ' its-notebook__punches--back' : ''}`;
          punches.setAttribute('aria-hidden', 'true');
          for (let i = 0; i < count; i++) {
            const pair = document.createElement('div');
            pair.className = 'its-notebook__punch-pair';
            pair.style.top = `${count === 1 ? 50 : 5 + i * 90 / (count - 1)}%`;
            punches.append(pair);
          }
          face.append(punches);
        });
      });
    }

    selectCover(button) {
      if (this.busy || this.drag || !this.coverOptions.includes(button)) return;
      if (button.getAttribute('aria-pressed') === 'true') return;
      const front = this.book.querySelector('[data-cover="front"]')?.querySelector('img');
      const back = this.book.querySelector('[data-cover="back"]')?.querySelector('img');
      if (!front || !back || !button.dataset.coverFront || !button.dataset.coverBack) return;
      [front, back].forEach((image, index) => {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.loading = 'eager';
        image.src = index ? button.dataset.coverBack : button.dataset.coverFront;
      });
      this.coverOptions.forEach(option => option.setAttribute('aria-pressed', String(option === button)));
      this.navigationTarget = null;
      this.preferredChapter = null;
      this.position = 0;
      this.render();
    }

    chapterPosition(button) {
      if (button.dataset.chapter === 'front') return 0;
      if (button.dataset.chapter === 'back') return this.maximum;
      const page = Number(button.dataset.chapter);
      return Number.isInteger(page) && page > 0 && page <= this.pages.length
        ? Math.min(this.maximum, Number(this.hasFrontCover) + Math.floor(page / 2)) : null;
    }

    navigateTo(button) {
      if (!this.chapters.includes(button)) return;
      const target = this.chapterPosition(button);
      if (target === null) return;
      this.preferredChapter = button;
      this.navigationTarget = target;
      this.navigationDuration = Math.max(180, Math.min(500, 1800 / Math.max(1, Math.abs(target - this.position))));
      this.advanceNavigation();
    }

    advanceNavigation() {
      if (this.navigationTarget == null || this.busy || this.drag) return;
      if (this.reducedMotion.matches) this.position = this.navigationTarget;
      if (this.position === this.navigationTarget) {
        this.navigationTarget = null;
        this.render();
        return;
      }
      const direction = Math.sign(this.navigationTarget - this.position);
      this.animate(this.prepare(direction), direction > 0 ? 0 : -180, direction > 0 ? -180 : 0, () => { this.position += direction; });
    }

    updateChapters(visiblePages) {
      let active;
      if (this.preferredChapter && this.chapterPosition(this.preferredChapter) === this.position) {
        active = this.preferredChapter;
      } else if (this.position === 0) {
        active = this.chapters.find(button => button.dataset.chapter === 'front');
      } else if (this.position === this.maximum) {
        active = this.chapters.find(button => button.dataset.chapter === 'back');
      } else {
        const page = visiblePages.length ? Math.max(...visiblePages) : Math.min(this.pages.length, (this.position - Number(this.hasFrontCover)) * 2);
        active = this.chapters.filter(button => Number(button.dataset.chapter) <= page).at(-1);
      }
      this.chapters.forEach(button => button.setAttribute('aria-current', button === active ? 'location' : 'false'));
    }

    updateWireMask(activeSheet, angle = 0) {
      if (!this.wire) return;
      this.wireMaskTurn = activeSheet ? { sheet: activeSheet, angle } : null;
      const covers = this.sheets.filter((sheet, index) => {
        if (!sheet.classList.contains('its-notebook__sheet--hard')) return false;
        return activeSheet ? sheet === activeSheet : index === this.position || index === this.position - 1;
      });
      if (!covers.length) {
        this.wire.style.maskImage = 'none';
        this.wire.style.webkitMaskImage = 'none';
        return;
      }
      const { width, height } = this.book.getBoundingClientRect();
      if (!width || !height) return;
      const span = Math.min(Math.max(16, width * .055), 44, height * .9 / this.wireCount * 64 / 28) * this.wireScale;
      const holeEdge = span * 25.5 / 64;
      const polygons = covers.map(sheet => {
        const turn = activeSheet ? angle : this.sheets.indexOf(sheet) < this.position ? -180 : 0;
        const radians = turn * Math.PI / 180;
        const cosine = Math.cos(radians);
        const sine = Math.sin(radians);
        const surface = cosine >= 0 ? 2 : -2;
        const project = (x, y) => {
          const depth = -sine * x + cosine * surface;
          const scale = 2200 / (2200 - depth);
          return `${((cosine * x + sine * surface) * scale + span / 2).toFixed(3)},${((y - height / 2) * scale + height / 2).toFixed(3)}`;
        };
        return `<polygon fill="black" points="${[project(holeEdge, 0), project(width / 2, 0), project(width / 2, height), project(holeEdge, height)].join(' ')}"/>`;
      }).join('');
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${span} ${height}" preserveAspectRatio="none"><defs><mask id="cover" maskUnits="userSpaceOnUse" x="0" y="0" width="${span}" height="${height}"><rect width="100%" height="100%" fill="white"/>${polygons}</mask></defs><rect width="100%" height="100%" fill="white" mask="url(#cover)"/></svg>`;
      const mask = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
      this.wire.style.maskImage = mask;
      this.wire.style.webkitMaskImage = mask;
    }

    addSheet(pages, hard = false) {
      const sheet = document.createElement('div');
      sheet.className = `its-notebook__sheet${hard ? ' its-notebook__sheet--hard' : ''}`;
      ['front', 'back'].forEach((side, index) => {
        const face = document.createElement('div');
        face.className = `its-notebook__face its-notebook__face--${side}`;
        if (pages[index]) face.append(pages[index]);
        sheet.append(face);
      });
      if (hard) {
        for (const side of ['outer', 'spine']) {
          const edge = document.createElement('div');
          edge.className = `its-notebook__cover-edge its-notebook__cover-edge--${side}`;
          edge.setAttribute('aria-hidden', 'true');
          sheet.append(edge);
        }
      }
      this.book.append(sheet);
      this.sheets.push(sheet);
    }

    disconnectedCallback() {
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
      this.drag = null;
      this.busy = false;
      this.navigationTarget = null;
      this.preferredChapter = null;
      this.classList.remove('is-dragging');
      this.clearCurve();
    }

    clearCurve() {
      if (!this.curve) return;
      this.curve.layer.remove();
      this.curve.sheet.classList.remove('is-curved');
      this.curve = null;
      this.book.style.setProperty('--cast-shadow', '0');
    }

    render() {
      this.clearCurve();
      const visiblePages = [];
      let visibleCover = '';
      this.sheets.forEach((sheet, index) => {
        sheet.style.setProperty('--turn', index < this.position ? '-180deg' : '0deg');
        sheet.style.zIndex = index < this.position ? index + 1 : this.sheets.length - index;
        sheet.classList.remove('is-turning');
        sheet.style.setProperty('--fold-shade', '0');
        [...sheet.children].slice(0, 2).forEach((face, side) => {
          const visible = side === 1 ? index === this.position - 1 : index === this.position;
          face.setAttribute('aria-hidden', String(!visible));
          if (visible) {
            const page = face.querySelector('[data-page]');
            if (page) visiblePages.push(Number(page.dataset.page));
            const cover = face.querySelector('[data-cover]');
            if (cover) visibleCover = cover.dataset.cover;
          }
          if (Math.abs(index - this.position) <= 1) {
            const image = face.querySelector('img');
            if (image) image.loading = 'eager';
          }
        });
      });
      if (visiblePages.length) {
        const current = visiblePages.join('–');
        this.status.textContent = this.dataset.statusTemplate.replace('[current]', current).replace('[total]', this.pages.length);
      } else {
        this.status.textContent = visibleCover === 'front' ? this.dataset.coverLabel : this.dataset.backCoverLabel;
      }
      this.previous.disabled = this.position === 0;
      this.next.disabled = this.position === this.maximum;
      this.updateChapters(visiblePages);
      this.updateBase(this.position);
      this.updateWireMask();
    }

    updateBase(position) {
      const left = this.hasFrontCover ? Math.max(0, -Math.cos(Math.min(1, position) * Math.PI)) : 1;
      const backProgress = Math.max(0, position - (this.sheets.length - 1));
      const right = this.hasBackCover ? Math.max(0, Math.cos(Math.min(1, backProgress) * Math.PI)) : 1;
      this.book.style.setProperty('--base-left', `calc(${50 * (1 - left)}% - ${5 * left}px)`);
      this.book.style.setProperty('--base-right', `calc(${50 * (1 - right)}% - ${5 * right}px)`);
      this.book.style.setProperty('--book-shift', `${25 * (left - right)}%`);
      this.wireScale = .58 + .42 * Math.min(left, right);
      this.book.style.setProperty('--wire-width', `${this.wireScale}`);
    }

    canTurn(direction) {
      return !this.busy && !this.drag && this.position + direction >= 0 && this.position + direction <= this.maximum;
    }

    prepare(direction) {
      const sheet = this.sheets[direction > 0 ? this.position : this.position - 1];
      sheet.style.zIndex = this.sheets.length + 2;
      sheet.classList.add('is-turning');
      this.updateWireMask(sheet, direction > 0 ? 0 : -180);
      if (!this.reducedMotion.matches && !sheet.classList.contains('its-notebook__sheet--hard')) {
        this.buildCurve(sheet, direction);
        this.paint(sheet, direction > 0 ? 0 : -180);
      }
      return sheet;
    }

    buildCurve(sheet, direction) {
      this.clearCurve();
      const width = this.book.getBoundingClientRect().width / 2;
      const height = this.book.getBoundingClientRect().height;
      const count = 24;
      const stripWidth = width / count;
      const layer = document.createElement('div');
      layer.className = 'its-notebook__curve';
      layer.setAttribute('aria-hidden', 'true');
      const faces = [...sheet.children];
      const strips = Array.from({ length: count }, (_, index) => {
        const strip = document.createElement('div');
        strip.className = 'its-notebook__strip';
        strip.style.width = `${stripWidth + 1}px`;
        faces.forEach((source, side) => {
          const face = document.createElement('div');
          face.className = `its-notebook__strip-face${side ? ' its-notebook__strip-face--back' : ''}`;
          const offset = -(side ? count - 1 - index : index) * stripWidth;
          const page = source.querySelector('.its-notebook__page');
          if (page) {
            const copy = page.cloneNode(true);
            copy.removeAttribute('data-page');
            copy.style.width = `${width}px`;
            copy.style.height = `${height}px`;
            copy.style.left = `${offset}px`;
            face.append(copy);
          }
          const punches = source.querySelector('.its-notebook__punches');
          if (punches && index * stripWidth < 23) {
            const copy = punches.cloneNode(true);
            copy.style.width = `${width}px`;
            copy.style.height = `${height}px`;
            copy.style.left = `${offset}px`;
            face.append(copy);
          }
          const paperShade = document.createElement('div');
          paperShade.className = `its-notebook__paper-shade${side ? ' its-notebook__paper-shade--back' : ''}`;
          paperShade.style.width = `${width}px`;
          paperShade.style.height = `${height}px`;
          paperShade.style.left = `${offset}px`;
          face.append(paperShade);
          strip.append(face);
        });
        layer.append(strip);
        return strip;
      });
      this.book.append(layer);
      sheet.classList.add('is-curved');
      this.curve = { sheet, layer, strips, stripWidth, direction };
    }

    paint(sheet, angle) {
      this.updateBase(this.sheets.indexOf(sheet) + Math.abs(angle) / 180);
      if (this.curve?.sheet === sheet) {
        const { strips, stripWidth, direction } = this.curve;
        const lift = Math.sin(Math.abs(angle) * Math.PI / 180);
        const bend = lift * 88 * direction;
        const rotation = angle + bend / 2;
        const rotationRadians = rotation * Math.PI / 180;
        const clearance = Math.min(4, stripWidth * .32);
        sheet.style.setProperty('--turn', `${rotation}deg`);
        let x = 0;
        let z = 0;
        strips.forEach((strip, index) => {
          const tangent = -bend * (index + .5) / strips.length;
          const radians = tangent * Math.PI / 180;
          strip.style.transform = `rotateY(${rotation}deg) translate3d(${x}px, 0, ${z}px) rotateY(${tangent}deg)`;
          const nextX = x + Math.cos(radians) * stripWidth;
          const nextZ = z - Math.sin(radians) * stripWidth;
          const heightStart = -Math.sin(rotationRadians) * x + Math.cos(rotationRadians) * z;
          const heightEnd = -Math.sin(rotationRadians) * nextX + Math.cos(rotationRadians) * nextZ;
          const raised = Math.min(heightStart, heightEnd) > clearance;
          strip.style.zIndex = this.sheets.length + (raised ? 4 : 2);
          const light = Math.sin((angle + bend / 2 + tangent) * Math.PI / 180);
          strip.style.setProperty('--strip-shade', Math.abs(light) * .19);
          x = nextX;
          z = nextZ;
        });
        this.book.style.setProperty('--cast-shadow', (lift * .22).toFixed(3));
        return;
      }
      sheet.style.setProperty('--turn', `${angle}deg`);
      sheet.style.setProperty('--fold-shade', (Math.sin(Math.abs(angle) * Math.PI / 180) * .14).toFixed(3));
      this.updateWireMask(sheet, angle);
    }

    animate(sheet, from, to, onFinish) {
      this.busy = true;
      const duration = this.reducedMotion.matches ? 0 : this.navigationTarget != null ? this.navigationDuration : Math.max(220, Math.abs(to - from) / 180 * 1000);
      const started = performance.now();
      const tick = now => {
        const progress = duration ? Math.min(1, (now - started) / duration) : 1;
        const ease = progress < .5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
        this.paint(sheet, from + (to - from) * ease);
        if (progress < 1) this.frame = requestAnimationFrame(tick);
        else {
          this.busy = false;
          onFinish();
          this.render();
          this.advanceNavigation();
        }
      };
      this.frame = requestAnimationFrame(tick);
    }

    turn(direction) {
      if (!this.canTurn(direction)) return;
      this.navigationTarget = null;
      this.preferredChapter = null;
      this.animate(this.prepare(direction), direction > 0 ? 0 : -180, direction > 0 ? -180 : 0, () => { this.position += direction; });
    }

    startDrag(event) {
      if (!event.isPrimary || event.button !== 0 || this.busy || this.drag) return;
      const rect = this.book.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) return;
      const direction = event.clientX >= rect.left + rect.width / 2 ? 1 : -1;
      if (!this.canTurn(direction)) return;
      this.preferredChapter = null;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, direction, width: rect.width / 2, progress: 0, active: false };
    }

    moveDrag(event) {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { this.drag = null; this.advanceNavigation(); return; }
        if (Math.abs(dx) < 6) return;
        drag.active = true;
        drag.sheet = this.prepare(drag.direction);
        this.stage.setPointerCapture(event.pointerId);
        this.classList.add('is-dragging');
      }
      drag.progress = Math.max(0, Math.min(1, -dx * drag.direction / drag.width));
      const angle = drag.direction > 0 ? -180 * drag.progress : -180 + 180 * drag.progress;
      this.paint(drag.sheet, this.reducedMotion.matches ? (drag.direction > 0 ? 0 : -180) : angle);
    }

    endDrag(event, cancelled = false) {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      this.drag = null;
      this.classList.remove('is-dragging');
      if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
      if (!drag.active) { this.advanceNavigation(); return; }
      const complete = !cancelled && drag.progress > .24;
      const start = drag.direction > 0 ? 0 : -180;
      const from = this.reducedMotion.matches ? start : start - drag.direction * 180 * drag.progress;
      const to = complete ? start - drag.direction * 180 : start;
      this.animate(drag.sheet, from, to, () => { if (complete) this.position += drag.direction; });
    }
  });
}
